import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PageContainer } from "@/features/ui";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import {
  DeliveryOrderPdf,
  GeneralInformationSection,
  ProductVariantsSection,
  StatusEnum,
} from "@/features/delivery-orders";
import { PermissionEnum } from "@/features/permissions";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  ResponsiveToolbarButton,
} from "@/features/ui";

export const DeliveryOrder = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);

  const loadDeliveryOrder = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`delivery-orders/${referenceNumber}`, {
        params: {
          withBuyer: true,
          withSeller: true,
          withWarehouse: true,
          withProductVariants: true,
          withTransactionHistories: true,
          withCreator: true,
        },
      });
      setDeliveryOrder(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat pengiriman"),
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadDeliveryOrder();
  }, [loadDeliveryOrder]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.READ_DELIVERY_ORDERS) &&
        (deliveryOrder.status === StatusEnum.DELIVERED ||
          deliveryOrder.status === StatusEnum.PARTIALLY_INVOICED) && (
          <ResponsiveToolbarButton
            label="Ubah"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate("edit")}
          />
        )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Pengiriman Penjualan"
      breadcrumbs={[
        { title: "Pengiriman Penjualan", path: "/sales/delivery-orders" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection deliveryOrder={deliveryOrder} />
        <ProductVariantsSection
          deliveryOrderProductVariants={
            deliveryOrder.deliveryOrderProductVariants
          }
          warehouse={deliveryOrder.warehouse}
        />

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Surat Jalan Pengiriman - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {deliveryOrder && user && (
              <PDFViewer width="100%" height="100%">
                <DeliveryOrderPdf
                  deliveryOrder={{
                    ...deliveryOrder,
                    deliveryOrderProductVariants: [
                      ...deliveryOrder.deliveryOrderProductVariants,
                    ],
                  }}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>

        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="DELIVERY_ORDER"
          transactionableId={deliveryOrder?.id}
        />
      </Stack>
    </PageContainer>
  );
};
